import { SelectFieldOption } from '@evorait/pace-components';
import { createSelector, MemoizedSelector } from '@ngrx/store';
import { ConfigConstants } from '../../../config/config-constants';
import { AddressState } from '../../address/address-collection.state';
import { isUserCreatedAddress } from '../../address/address-number';
import { selectVisibleAddresses } from '../../address/selectors/address.selector';
import { selectActiveNotificationCustomerAddressIds } from '../../notification/selectors/notification.selector';
import { selectValueHelpersById } from '../../value-helper/selectors/value-helper.selector';
import { ValueHelperState } from '../../value-helper/value-helper-collection.state';
import { selectSelectedTimesheetMerged } from './timesheet-merge.selector';

/**
 * The option lists and code→description lookups the timesheet rows need, projected straight from
 * the value-help slice.
 *
 * The row components take `SelectFieldOption[]`, which is deliberately domain-agnostic, so the
 * mapping from value-help data onto it lives here rather than in the components — and the
 * components never inject `ValueHelperService`.
 */

/**
 * Maps value-help entries onto the plain option shape the selection fields consume.
 *
 * @param valueHelpers the value-help entries for one list
 * @returns the options, in the order the value-help slice holds them
 */
const toOptions = (valueHelpers: ValueHelperState[]): SelectFieldOption[] =>
    valueHelpers.map((valueHelper) => ({ value: valueHelper.code, label: valueHelper.descText }));

/**
 * Maps value-help entries onto a code→description lookup.
 *
 * @param valueHelpers the value-help entries for one list
 * @returns descriptions keyed by their code
 */
const toDescriptions = (valueHelpers: ValueHelperState[]): Record<string, string> =>
    Object.fromEntries(valueHelpers.map((valueHelper) => [valueHelper.code, valueHelper.descText]));

const selectTimesheetGapTypes = selectValueHelpersById(ConfigConstants.valueHelperIDTimesheetGapTypeInfo);
const selectTimeRecordingTaskTypes = selectValueHelpersById(ConfigConstants.valueHelperIDTimeRecordingTaskTypeInfo);
const selectMeansOfTransportation = selectValueHelpersById(ConfigConstants.valueHelperIDMeansOfTransportation);
const selectStatesOfUSA = selectValueHelpersById(ConfigConstants.valueHelperIDStatesOfUSA);

/**
 * The US states the address form offers, in the value help's own order. Mirrors
 * `ValueHelperService.getStatesOfUSA`, which the legacy address form reached through
 * `AddressModel.updateStateOfUSA`; the form shows the field only where
 * `selectEnableStatesOfUSADropdown` is on, exactly as that method decided whether to build the
 * dropdown at all.
 */
export const selectStatesOfUSAOptions: MemoizedSelector<object, SelectFieldOption[]> = createSelector(
    selectStatesOfUSA,
    toOptions,
);

/**
 * Task types a manual entry can be given — the gap task types, covering both productive and
 * non-productive work. Mirrors `ValueHelperService.getTimesheetGapTypeInfo`.
 */
export const selectTimesheetTaskTypeOptions: MemoizedSelector<object, SelectFieldOption[]> = createSelector(
    selectTimesheetGapTypes,
    toOptions,
);

/** Means of transport a travel entry can be given. */
export const selectMeansOfTransportOptions: MemoizedSelector<object, SelectFieldOption[]> = createSelector(
    selectMeansOfTransportation,
    toOptions,
);

/**
 * The addresses the timesheet's address picker offers, each carrying whether it may be hidden.
 *
 * `allowToHide` is recomputed here rather than read from the slice, and it is the replacement for
 * the legacy `AddressSelectComponent.setAllowToHidePropertyForAddresses`, which walked the timesheet
 * entries and mutated the flag on the address objects. Two rules, both from that method: only an
 * address the backend supplied — a customer address or an `AddressSet` row, i.e. anything that is
 * not {@link isUserCreatedAddress} — may be hidden at all, and never one the timesheet is currently
 * using — hiding that would strand the entry that points at it. Now that an entry stores the
 * address's id rather than a copy of it, "currently using" is a direct id comparison rather than an
 * `addrNumber` match.
 *
 * A third rule joins those two: never the customer address of a notification the device currently
 * has (see {@link selectActiveNotificationCustomerAddressIds}). That address is where the job is —
 * hiding it would take the site of active work out of the picker, and no timesheet entry has to
 * point at it yet for that to be wrong.
 *
 * A user-created address needs no special case: {@link isUserCreatedAddress} already excludes it
 * from the first rule regardless of whether its id is in use. The picker offers it "Delete" instead
 * (`AddressPickerComponent.canDelete`), since hiding an address only this device knows would make it
 * unreachable.
 *
 * Reads the merged timesheet: a row derived from an open confirmation points at an address just as a
 * committed one does, and hiding it would strand that row too.
 */
export const selectTimesheetAddressChoices: MemoizedSelector<object, AddressState[]> = createSelector(
    selectVisibleAddresses,
    selectSelectedTimesheetMerged,
    selectActiveNotificationCustomerAddressIds,
    (addresses, timesheet, notificationAddressIds): AddressState[] => {
        const addressIdsInUse = new Set(
            (timesheet?.entries ?? [])
                .map((entry) => entry.address)
                .filter((addressId): addressId is string => Boolean(addressId)),
        );

        return addresses.map((address) => ({
            ...address,
            allowToHide:
                !isUserCreatedAddress(address) &&
                !addressIdsInUse.has(address.id) &&
                !notificationAddressIds.has(address.id),
        }));
    },
);

/**
 * Description for any work code a row can carry, looked up by code.
 *
 * Both value-help lists are folded together because that is what the legacy `getDesc` searched:
 * the gap task types first (they cover manual entries and non-productive work), then the
 * time-recording task types. Gap types win a collision, matching the legacy lookup order.
 */
export const selectTimesheetWorkCodeDescriptions: MemoizedSelector<object, Record<string, string>> = createSelector(
    selectTimeRecordingTaskTypes,
    selectTimesheetGapTypes,
    (timeRecordingTaskTypes, gapTypes) => ({
        ...toDescriptions(timeRecordingTaskTypes),
        ...toDescriptions(gapTypes),
    }),
);
